/** Service Desk console: Orders, Quotes, Approvals and the order detail (SD-S2-21, WP-S2-H). */
export const serviceOrders = {
  title: "Orders",
  subtitle: "Orders placed in chat and the web app, or by staff for a client, written back to your order system. Quotes and approvals have their own tabs.",
  tabs: { label: "Orders sections", orders: "Orders", quotes: "Quotes", approvals: "Approvals" },
  actions: {
    newOrder: "New order on behalf",
    newQuote: "New quote",
    retry: "Retry sync",
    retryFor: "Retry sync for {{number}}",
    retryNow: "Retry now",
    markPlaced: "Mark placed with reference",
    markPlacedFor: "Mark {{number}} placed with a reference",
    cancel: "Cancel order",
    reject: "Reject order",
    refresh: "Refresh",
  },
  status: {
    draft: "Draft",
    pending_confirmation: "Awaiting confirmation",
    pending_approval: "Waiting for approval",
    approved: "Approved",
    submitting: "Sending",
    placed: "Placed",
    processing: "Processing",
    partially_shipped: "Partly shipped",
    shipped: "Shipped",
    delivered: "Delivered",
    completed: "Completed",
    declined: "Declined",
    cancel_requested: "Cancellation requested",
    cancelled: "Cancelled",
    rejected: "Rejected",
    sync_failed: "Sync failed",
  },
  quoteStatus: { requested: "Waiting for prices", quoted: "Quoted", accepted: "Accepted", declined: "Declined", expired: "Expired", converted: "Ordered" },
  approvalStatus: { pending: "Waiting for approval", approved: "Approved", declined: "Declined", expired: "Expired", withdrawn: "Withdrawn" },
  approvalReason: {
    contact_limit: "Over the contact's own limit",
    org_threshold: "Over the organisation's threshold",
    staff_threshold: "Over the staff approval threshold",
    credit_status: "Organisation on credit hold",
    line_category: "A product that always needs approval",
    anomaly: "Unusually large for this organisation",
  },
  source: { cart: "Client", staff: "Staff on behalf" },
  stock: { in_stock: "In stock", low: "Low stock", out: "Out of stock", made_to_order: "Made to order" },
  actor: { contact: "The client", staff: "A team member", system: "Sagot" },
  stats: {
    label: "Order totals",
    attention: "Needs attention",
    attentionHint: "sync failed, waiting for approval or a cancellation request",
    active: "In progress",
    activeHint: "approved, sent, processing or shipped",
    done: "Delivered",
    doneHint: "delivered or completed",
    closed: "Closed",
    closedHint: "cancelled, rejected or declined",
  },
  banner: {
    failed_one: "{{count}} order could not be sent to your order system.",
    failed_other: "{{count}} orders could not be sent to your order system.",
    failedBody: "The client was told the team is on it. Retry, record the order as placed, or cancel it.",
    show: "Show them",
    waiting_one: "{{count}} order is waiting for an approver.",
    waiting_other: "{{count}} orders are waiting for an approver.",
    open: "Open approvals",
    syncFailedTitle: "{{number}} could not be sent to your order system.",
    syncFailedBody: "{{name}} was told the team is on it. Choose what happens next.",
    manualTitle: "This order waits for you.",
    manualBody: "Your catalogue is a CSV, so nothing is sent for you. Key the order into your system, then record its number here, or reject it with a reason.",
    cancelRequestedTitle: "The client asked to cancel this order.",
    cancelRequestedBody: "Accepting cancels it and tells the client. Declining lets it go on.",
    cancelled: "This order was cancelled.",
    declined: "This order was declined by its approver.",
    rejected: "This order was rejected.",
  },
  filters: {
    group: "Order status filter",
    all: "All",
    search: "Search order number, PO or reference",
    searchLabel: "Search orders",
    organisation: "Organisation",
    allOrganisations: "All organisations",
    clear: "Clear filters",
  },
  views: {
    group: "Saved views",
    saveCurrent: "Save this view",
    saveTitle: "Save this view",
    saveBody: "Keeps the status, organisation and search you have now, in this browser.",
    name: "Name of the view",
    save: "Save view",
    problem: "Give the view a name.",
    remove: "Remove the view {{name}}",
  },
  list: {
    label: "Orders",
    order: "Order",
    organisation: "Organisation",
    lines: "Lines",
    lineCount_one: "{{count}} line",
    lineCount_other: "{{count}} lines",
    po: "PO",
    total: "Total",
    status: "Status",
    source: "Placed by",
    created: "Created",
    notInSystem: "Not in your system yet",
    individual: "Individual client",
    showing: "Showing {{shown}} of {{total}}",
    loadMore: "Load more",
  },
  empty: {
    title: "No orders yet",
    body: "When clients order in chat or the web app, or staff place an order for them, it shows up here with its status.",
    filteredTitle: "No orders match these filters",
    filteredBody: "Try a different status, organisation or search.",
  },
  error: { title: "We couldn't load this", body: "Check your connection and try again. Nothing was changed." },
  retry: { sent: "{{number}} was sent to your order system.", stillFailed: "{{number}} still could not be sent. The reason is in its connector calls." },
  markPlaced: {
    title: "Mark {{number}} as placed",
    description: "Use this when you keyed the order into your own system and it gave you a number.",
    reference: "Reference in your system",
    referenceHint: "The order number your system shows, for example SO-2026-00880.",
    checked: "I checked my system: this order exists and matches the lines and total.",
    confirm: "Mark as placed",
    done: "{{number}} is recorded as placed.",
    problem: { empty: "Enter the reference your system gave the order.", tooLong: "Use {{max}} characters or fewer.", characters: "Use letters, numbers and - _ . / # only.", unchecked: "Confirm that you checked your system." },
  },
  cancel: {
    title: "Cancel {{number}}",
    description: "The client is told, and your system is asked to cancel the order when it supports it.",
    warning: "This cannot be undone.",
    reason: "Reason",
    choose: "Choose a reason",
    reasons: { credit_hold: "Credit hold at the order system", client_asked: "The client asked to cancel", unavailable: "An item is no longer available", duplicate: "Duplicate order", other: "Other" },
    note: "Note for the record (optional)",
    keep: "Keep order",
    confirm: "Cancel order",
    done: "{{number}} was cancelled.",
    problem: "Choose a reason for cancelling.",
  },
  reject: {
    title: "Reject {{number}}",
    description: "Refuse the order. The client reads the reason in plain words.",
    reason: "Why it was refused",
    reasons: { out_of_stock: "Out of stock", price_changed: "The price changed", credit_hold: "Credit hold", invalid_customer: "Customer not set up in the system", other: "Other" },
    confirm: "Reject order",
    done: "{{number}} was rejected.",
  },
  cancelRequest: {
    acceptTitle: "Accept the cancellation of {{number}}",
    declineTitle: "Decline the cancellation of {{number}}",
    acceptBody: "The order is cancelled and the client is told.",
    declineBody: "The order goes on and the client is told it could not be cancelled.",
    note: "Note for the client (optional)",
    noteHint: "Shown to the client with the decision.",
    accept: "Accept cancellation",
    decline: "Decline cancellation",
    accepted: "{{number}} was cancelled.",
    declined: "The request to cancel {{number}} was declined.",
  },
  new: {
    title: "New order on behalf",
    description: "Same checks as the client's own order: their limit, PO rule, stock and price list apply.",
    place: "Place order",
    po: "PO number",
    poHint: "Optional unless the organisation requires one.",
    notes: "Notes",
    notesHint: "Up to {{max}} characters.",
    rules: "The order is priced live and confirmed on the client's behalf. If it is above their limit it goes to an approver instead of your system.",
    done: "{{number}} was created.",
    problem: { organisation: "Choose the organisation.", contact: "Choose who the order is for.", lines: "Add at least one product." },
  },
  newQuote: {
    title: "New quote",
    description: "Enter a quotation for a client: lines, prices and how long they hold.",
    days: "Valid for (days)",
    daysHint: "From 1 to {{max}} days.",
    notes: "Internal notes",
    notesHint: "Only the team sees these.",
    send: "Send it to the client now",
    sendHint: "Every line needs a price. Otherwise the quote is saved and sent when you price it.",
    submit: "Save quote",
    submitSend: "Send quote",
    saved: "Quote {{number}} was saved.",
    sent: "Quote {{number}} was sent.",
    problem: { contact: "Choose who the quote is for.", lines: "Add at least one line.", days: "Enter a whole number of days from 1 to {{max}}.", priced: "Give every line a price before sending." },
  },
  picker: {
    organisation: "Organisation",
    chooseOrganisation: "Choose an organisation",
    contact: "Contact",
    chooseContact: "Choose a contact",
    nobody: "No contact of this organisation may order. Allow ordering on the contact first.",
  },
  lines: {
    title: "Lines",
    empty: "No lines yet. Search the catalogue and add a product.",
    search: "Search the catalogue",
    searchHint: "SKU, name or model",
    product: "Product",
    choose: "Choose a product",
    noMatch: "No product matches",
    searchFailed: "The catalogue could not be searched.",
    add: "Add",
    described: "Describe an item in words",
    describedHint: "For something not in the catalogue. You price it yourself.",
    noSku: "No SKU",
    price: "Unit price",
    qtyFor: "Quantity of {{name}}",
    priceFor: "Unit price of {{name}}",
    remove: "Remove {{name}}",
  },
  quotes: {
    label: "Quotes",
    quote: "Quote",
    validUntil: "Valid until",
    organisation: "Organisation",
    contact: "Contact",
    ticket: "Ticket",
    reference: "System reference",
    preview: "Open quote {{number}}",
    source: { cart: "From a cart", text: "Client request", staff: "Entered by staff" },
    emptyTitle: "No quotes yet",
    emptyBody: "Quote requests from clients and quotations entered by staff appear here.",
    drawerTitle: "Quote {{number}}",
    loading: "Quote",
    lines: "Quote lines",
    byHand: "Priced by hand",
    declinedWhy: "Declined: {{reason}}",
    orderMade: "Order made from this quote:",
    notes: "Internal notes",
    saveNotes: "Save notes",
    notesSaved: "Notes saved.",
    priceTitle: "Give this quote its prices",
    repriceTitle: "Replace the prices",
    sendPrices: "Send prices to the client",
    priced: "Quote {{number}} was priced and sent.",
    problem: { price: "Give every line a price in pesos, with up to two decimals.", days: "Enter how many days the prices hold." },
    decline: "Decline quote",
    declineReason: "Reason (optional)",
    declineHint: "The client is told the quote was declined.",
    declined: "Quote {{number}} was declined.",
    notFoundTitle: "We can't find this quote",
    notFoundBody: "It may have been removed. Go back to the list.",
  },
  approvals: {
    label: "Order approvals",
    filter: "Approval status",
    allStatuses: "All approvals",
    order: "Order",
    requestedBy: "Requested by",
    amount: "Amount",
    reasons: "Why",
    decision: "Decision",
    action: "Action",
    side: { client: "Client approver", staff: "Staff approver" },
    review: "Review",
    reviewFor: "Review the approval for {{number}}",
    decidedAt: "{{when}} by {{who}}",
    expires: "Expires {{when}}",
    emptyTitle: "No approvals",
    emptyBody: "Orders above a client's limit, or that need a staff decision, appear here.",
    nothingTitle: "Nothing is waiting for an approver.",
    nothingBody: "Clients approve in their portal. Staff only step in above the staff threshold or for organisations on credit hold.",
    title: "Approval for {{number}}",
    loading: "Approval",
    description: "Look at the figures and why it was asked for, then decide.",
    lines: "Approval lines",
    total: "Total {{total}}",
    repriced: "Prices were read again: the total is now {{total}} ({{percent}}% higher). Look at it before you approve.",
    outOfStock: "Out of stock now: {{skus}}",
    seen: "I have seen the new prices.",
    note: "Note",
    noteHint: "Required when you decline. Shown on the order.",
    approve: "Approve",
    decline: "Decline",
    approved: "{{number}} was approved and sent on.",
    declined: "{{number}} was declined.",
    settledTitle: "Already decided",
    settledBody: "Someone else decided this approval. Close this and refresh the list.",
    problem: { unseen: "Tick that you have seen the new prices.", reason: "Say why you decline." },
  },
  trail: {
    title: "Approval trail",
    client: "The client",
    confirmed: "{{who}} confirmed the order ({{total}}).",
    asked: "Needs an approver: {{reasons}}.",
    approved: "Approved by {{who}}.",
    declined: "Declined by {{who}}.",
    expired: "The approval ran out before anyone decided.",
    waiting: "Waiting for an approver. Expires {{when}}.",
    review: "Review and decide",
    failed: "The approval trail could not be loaded.",
  },
  detail: {
    back: "Orders",
    loading: "Loading the order",
    notFoundTitle: "We can't find this order",
    notFoundBody: "Check the number, or go back to the Orders list.",
    lines_one: "Lines · {{count}} item",
    lines_other: "Lines · {{count}} items",
    linesLabel: "Order lines",
    product: "Product",
    qty: "Qty",
    unitPrice: "Unit price",
    lineTotal: "Line total",
    shipped_one: "{{count}} shipped",
    shipped_other: "{{count}} shipped",
    listPrice: "List price",
    subtotal: "Subtotal",
    vat: "VAT",
    vatEstimated: "VAT (estimated)",
    vatIncluded: "Prices include VAT.",
    vatExcluded: "Prices exclude VAT.",
    total: "Total",
    progress: "Order progress",
    timeline: "Status timeline",
    noEvents: "Nothing has happened yet.",
    staffOnly: "Staff only",
  },
  progress: { confirmed: "Confirmed", approval: "Approval", sent: "Sent to your system", processing: "Processing", shipped: "Shipped", delivered: "Delivered", closed: "Closed", state: { done: "done", current: "in progress", failed: "failed", todo: "not yet" } },
  event: {
    created: "Order created",
    reviewed: "Reviewed by the client",
    price_changed: "A price changed",
    confirmed: "Confirmed",
    status_changed: "Status changed from {{from}} to {{to}}",
    approval_required: "Sent for approval",
    queued: "Queued to send",
    note: "Note added",
    approval_decided: "Approval decided",
    submit_attempt: "Tried to send to your system",
    reconciled: "Checked your system before retrying",
    external_status: "Your system reported a new status",
    delayed: "Running late",
  },
  writeback: {
    title: "Write-back to your system",
    notSent: "Not sent yet",
    placed: "Placed",
    waitingApproval: "Nothing is sent until the approver decides.",
    reference: "Reference",
    none: "None yet",
    copy: "Copy the reference",
    retryPlan: "Retry plan",
    retryBody: "Your system is asked first on every retry, so an order is never created twice.",
    stopped: "Automatic retries have stopped. A person has to decide.",
    calls: "Connector calls",
    openLog: "Open the connector call log",
    time: "Time",
    operation: "Operation",
    result: "Result",
    latency: "Latency",
    callStatus: { ok: "OK", failed: "Failed" },
    noCalls: "No calls recorded for this order in the latest connector log.",
    callsFailed: "The connector calls could not be loaded.",
    noConnector: "No connector is set up yet. Connect one under Commerce profile.",
    noAccess: "You need access to connectors to see the calls made for this order.",
  },
  info: { title: "Order", organisation: "Organisation", contact: "Contact", po: "PO number", site: "Delivery site", source: "Placed by", created: "Created", requestedDate: "Wanted by", notes: "Notes" },
  confirmation: {
    title: "Confirmation record",
    method: "How",
    methods: { card: "The client pressed Confirm", staff_on_behalf: "Staff on the client's behalf" },
    verification: "Verified by",
    at: "Confirmed",
    stepUp: "Step-up code",
    noStepUp: "Not required",
    hash: "Cart hash",
    copyHash: "Copy the cart hash",
    immutable: "This record is written once and can't be edited.",
    none: "The client has not confirmed this order yet.",
  },
  fulfilment: { title: "Fulfilment", delayed: "Delayed", tracking: "Tracking", carrier: "Carrier", expected: "Expected delivery", invoices: "Invoices", checked: "Last checked" },
  tickets: {
    title: "Linked tickets",
    none: "No tickets about this order.",
    failed: "The linked tickets could not be loaded.",
    kind: { order_issue: "Order issue", requested_via: "Requested via" },
    issue: { wrong_item: "Wrong item", missing_quantity: "Missing quantity", damaged: "Damaged", late: "Late", invoice: "Invoice", other: "Other" },
    create: "Create ticket",
    linkExisting: "Link ticket",
    linkTitle: "Link a ticket to {{number}}",
    linkBody: "The ticket must belong to the same client.",
    number: "Ticket number",
    link: "Link ticket",
    linkProblem: "Enter a ticket number such as TKT-1001.",
    linked: "Ticket {{ticket}} is linked to {{number}}.",
  },
  orgTab: { viewAll: "View all", emptyTitle: "No orders yet", emptyBody: "Orders this organisation places appear here." },
} as const;
